import Alert, { AlertError } from 'components/alert/Alert';
import NavBackButton from 'components/buttons/NavBackButton';
import { Headline4, BodyLarge, FlexMd } from 'components/Elements';
import useNeveForm from 'components/forms/NeveForm';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import useDeleteRequest from 'hooks/useDeleteRequest';
import useModifyRequest from 'hooks/useModifyRequest';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import React from 'react';
import { breakpoints } from 'theme';
import { GenerateTypeContentComponent } from '../GenerateTypeContentComponent';
import strings from '../../../../../strings/strings.json';
import Hint from 'components/Hint';
import TextAreaField from 'components/forms/TextAreaField';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import { GenerateErrorReason, GenerateType } from 'models/Activity';
import AudioPlayer from 'pages/learner/components/AudioPlayer';
import VideoPlayer from 'pages/learner/components/VideoPlayer';
import {
    ReviewGeneratedContentProps,
    Container,
    ContentCard,
    ApproveFormCard,
    ErrorRow,
} from './ReviewGeneratedSharedComponents';
import styled from 'styled-components';
import Button from 'components/buttons/Button';

export default function ReviewGeneratedTextComponent({ item, backButton }: ReviewGeneratedContentProps): JSX.Element {
    const context = React.useContext(BuildCourseContext);
    const [showError, setShowError] = React.useState<boolean>();
    const [openRejectAlert, setOpenRejectAlert] = React.useState(false);
    const [alertError, setAlertError] = React.useState<AlertError>();
    const [getTranscriptError, setGetTranscriptError] = React.useState<boolean>(false);

    const { modifyData: approveText, loading: approving } = useModifyRequest(
        `activities/task/${item.activityId}/approve-text`,
        'PUT',
    );
    const { deleteData: rejectStatus, deleteInProgress: rejecting } = useDeleteRequest(
        `generate-content-statuses/${item.id}`,
    );

    type ApproveTextInput = {
        content: string;
    };

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useNeveForm<ApproveTextInput>({
        content: '',
    });

    React.useEffect(() => {
        const getTranscript = async () => {
            try {
                const response = await fetch(`${DOCUMENTS_URL}/${item.textContent}`);
                if (!response.ok) {
                    setGetTranscriptError(true);
                }

                const data = await response.json();
                const transcript: string | null = data?.results?.transcripts[0]?.transcript;
                if (!transcript) {
                    setGetTranscriptError(true);
                    return;
                }

                setValue('content', transcript);
            } catch (error) {
                setGetTranscriptError(true);
            }
        };

        getTranscript();
    }, [item.textContent]); // eslint-disable-line

    const approveClick = async (formData: ApproveTextInput) => {
        const { value, errors } = await approveText({
            content: formData.content,
        });

        if ((errors && errors.length > 0) || (!value as boolean)) {
            setShowError(true);
            return;
        }

        if (!context.editMode) {
            await context.refetchCourse();
            backButton();
            return;
        }

        context.setCourseState((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                modules: prev.modules.map((module) => ({
                    ...module,
                    activities: module.activities.map((activity) => {
                        if (activity.id !== item.activityId) return activity;

                        let status = activity.generateContentStatus;
                        if (status) {
                            status = {
                                ...status,
                                dateGenerated: new Date(),
                                dateApproved: new Date(),
                                dateErrored: null,
                                error: GenerateErrorReason.NONE,
                            };
                        }

                        return {
                            ...activity,
                            content: formData.content,
                            generateContentStatus: status,
                        };
                    }),
                })),
            };
        });

        backButton();
    };

    const rejectClick = async () => {
        const { value, errors } = await rejectStatus();

        if ((errors && errors.length > 0) || (value as boolean) === false) {
            const error: AlertError = {
                title: strings.courseAIGeneratedContentComponent.rejectAlert.alertErrorTitle,
                description: strings.courseAIGeneratedContentComponent.rejectAlert.alertErrorDescription,
            };
            setAlertError(error);
        } else {
            await context.refetchGenerateContentStatuses();
            backButton();
        }
    };

    if (getTranscriptError) {
        <Container>
            <NavBackButton
                backButtonTitle={''}
                backButtonAria={strings.courseAIGeneratedContentComponent.backButtonAria}
                backButtonHref={''}
                buttonFunctionOverride={backButton}
            />
            <GenerateTypeContentComponent generateType={item.generateType} />
            <Headline4>{`${strings.courseAIGeneratedContentComponent.activityPrefix} ${item.activityOrder}: ${item.activityTitle}`}</Headline4>
            <BodyLarge>{strings.courseAIGeneratedContentComponent.contentTitle}</BodyLarge>
            <ErrorNotifier
                title={strings.courseAIGeneratedContentComponent.reviewGeneratedTextComponent.error.title}
                description={strings.courseAIGeneratedContentComponent.reviewGeneratedTextComponent.error.description}
            />
        </Container>;
    }

    const content = watch('content');
    const contentMaxLength = 10000;

    let hintText: string = '';
    let mediaPlayer: JSX.Element = <></>;

    switch (item.generateType) {
        case GenerateType.AUDIO_TO_TEXT:
            hintText = strings.courseAIGeneratedContentComponent.audioToTextHint;

            const audioPath = !context.editMode
                ? item.mediaContent
                : context.course!.modules.flatMap((m) => m.activities).find((a) => a.id === item.activityId)
                      ?.audioDocument?.s3Filepath;

            mediaPlayer = <AudioPlayer filepath={audioPath!} />;
            break;
        case GenerateType.VIDEO_TO_TEXT:
            hintText = strings.courseAIGeneratedContentComponent.videoToTextHint;

            const videoPath = !context.editMode
                ? item.mediaContent
                : context.course!.modules.flatMap((m) => m.activities).find((a) => a.id === item.activityId)
                      ?.videoDocument?.s3Filepath;

            mediaPlayer = <VideoPlayer filepath={videoPath!} />;
            break;
    }

    return (
        <>
            {openRejectAlert && (
                <Alert
                    buttonText={strings.courseAIGeneratedContentComponent.rejectAlert.alertButton}
                    ariaButtonText={strings.courseAIGeneratedContentComponent.rejectAlert.alertButtonAria}
                    alertText={strings.courseAIGeneratedContentComponent.rejectAlert.alertText}
                    closeAlert={() => setOpenRejectAlert(false)}
                    onSubmit={rejectClick}
                    submitting={rejecting}
                    error={alertError}
                />
            )}
            <Container>
                <NavBackButton
                    backButtonTitle={''}
                    backButtonAria={strings.courseAIGeneratedContentComponent.backButtonAria}
                    backButtonHref={''}
                    buttonFunctionOverride={backButton}
                />
                <GenerateTypeContentComponent generateType={item.generateType} />

                <Headline4>{`${strings.courseAIGeneratedContentComponent.activityPrefix} ${item.activityOrder}: ${item.activityTitle}`}</Headline4>
                <ContentCard>
                    <Hint hint={hintText} />
                    {mediaPlayer}

                    <ApproveFormCard onSubmit={handleSubmit(approveClick)}>
                        <TextContentContainer>
                            <TextAreaField
                                fieldName={
                                    strings.courseAIGeneratedContentComponent.reviewGeneratedTextComponent
                                        .contentTextAreaField.name
                                }
                                maxLength={contentMaxLength}
                                charactersLeft={contentMaxLength - content.length}
                                aria={
                                    strings.courseAIGeneratedContentComponent.reviewGeneratedTextComponent
                                        .contentTextAreaField.aria
                                }
                                errorMessage={errors.content?.message}
                                required
                                min-height={400}
                                {...register('content', {
                                    required: {
                                        value: true,
                                        message: requiredFieldErrorMessage(
                                            strings.courseAIGeneratedContentComponent.reviewGeneratedTextComponent
                                                .contentTextAreaField.name,
                                        ),
                                    },
                                    maxLength: {
                                        value: contentMaxLength,
                                        message: createErrorMessage(
                                            strings.courseAIGeneratedContentComponent.reviewGeneratedTextComponent
                                                .contentTextAreaField.name,
                                            fieldErrorCodes.maxLength,
                                        ),
                                    },
                                })}
                            />
                        </TextContentContainer>

                        <ButtonsRow>
                            <Button
                                title={strings.courseAIGeneratedContentComponent.rejectButton.title}
                                aria={strings.courseAIGeneratedContentComponent.rejectButton.aria}
                                onClick={() => setOpenRejectAlert(true)}
                                colour={'BLACK'}
                            />
                            <Button
                                title={strings.courseAIGeneratedContentComponent.approveTranscriptButton.title}
                                aria={strings.courseAIGeneratedContentComponent.approveTranscriptButton.aria}
                                type="submit"
                                submitting={approving}
                            />
                        </ButtonsRow>
                    </ApproveFormCard>
                    {showError && (
                        <ErrorRow>
                            <ErrorNotifierSlim description={strings.courseAIGeneratedContentComponent.approveError} />
                        </ErrorRow>
                    )}
                </ContentCard>
            </Container>
        </>
    );
}

const ButtonsRow = styled(FlexMd)`
    justify-content: end;
    width: 100%;

    @media (max-width: ${breakpoints.md}) {
        flex-direction: column;
    }
`;

const TextContentContainer = styled.div`
    width: 100%;
`;
